import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import "./index.scss";
import * as _ from "lodash";
import { useTranslation } from "react-i18next";
import { listProfileBusinessHourByProfileId } from "../../../../../../store/profile-business/thunkAction";
import {
  BusinessDates,
  PositionWindow,
  ScheduleType,
  SizeWindow,
} from "../../../../../../constant/enums";
import {
  addProfileAppearance,
  editProfileAppearance,
} from "../../../../../../store/profile-appearance/thunkAction";
import {
  editProfile,
  readProfile,
} from "../../../../../../store/profile/thunkAction";
import {
  sendNotifyIziMessageError,
  sendNotifyIziMessageSuccess,
} from "../../../../../../utils/event";

const REGEX_NUMBER = /^\d+$/;

const AppearanceWindow = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const params = useParams();
  const profileId = _.get(params, "id");

  const [color, setColor] = useState("");
  const [status, setStatus] = useState("");
  const [size, setSize] = useState(SizeWindow.MEDIUM);
  const [position, setPosition] = useState(PositionWindow.LEFT);
  const [offset, setOffset] = useState(0);
  const [schedule, setSchedule] = useState(ScheduleType.ENTIRE_DAY);

  useEffect(() => {
    dispatch(readProfile(profileId));
    dispatch(listProfileBusinessHourByProfileId(profileId));
  }, [profileId, dispatch]);

  const profileAppearanceManage = useSelector(
    (state) => state.profileAppearanceManage
  );
  const profileBusinessManage = useSelector(
    (state) => state.profileBusinessManage
  );
  const profileManage = useSelector((state) => state.profileManage);
  const profile = _.get(profileManage, "profile");
  const profileBusinessHours = _.get(
    profileBusinessManage,
    "profileBusinessHours"
  );
  const businessHours = _.groupBy(profileBusinessHours, "date");
  const dayKeys = _.keys(BusinessDates);
  const profileAppearance = _.get(profileAppearanceManage, "profileAppearance");
  const profileAppearanceId = _.get(profileAppearance, "_id");
  const isLoadingProfileAppearance = _.get(
    profileAppearance,
    "isLoadingProfileAppearance",
    false
  );

  useEffect(() => {
    setSchedule(_.get(profile, "schedule", ScheduleType.ENTIRE_DAY));
  }, [profile]);

  useEffect(() => {
    const window = _.get(profileAppearance, "window", {});

    setColor(_.get(window, "color"));
    setSize(_.get(window, "size", SizeWindow.MEDIUM));
    setPosition(_.get(window, "position", PositionWindow.LEFT));
    setStatus(_.get(window, "status"));
    setOffset(_.get(window, "offset"));
  }, [profileAppearance]);

  const validateProfileAppearance = () => {
    if (!REGEX_NUMBER.test(offset)) {
      sendNotifyIziMessageError(t("profile.offset_is_number"));
      return false;
    }
    return true;
  };

  const saveWindow = async () => {
    const isOk = validateProfileAppearance();
    if (!isOk) {
      return;
    }
    const window = {
      color,
      status,
      size,
      position,
      offset,
    };

    try {
      dispatch(editProfile({ id: profileId, data: { schedule: schedule } }));

      if (profileAppearanceId) {
        await dispatch(
          editProfileAppearance({
            id: profileAppearanceId,
            data: {
              window,
            },
            isFormData: false,
          })
        ).unwrap();
        sendNotifyIziMessageSuccess(
          t("profile.profile_appearance_save_success")
        );
        return;
      }

      const data = {
        profileId,
        window,
        chatBadge: {},
        content: {},
      };

      if (isLoadingProfileAppearance) {
        return;
      }

      await dispatch(
        addProfileAppearance({ data, isFormData: false })
      ).unwrap();
      sendNotifyIziMessageSuccess(t("profile.profile_appearance_save_success"));
    } catch (error) {
      const errorCode = _.get(error, "errorCode");
      if (errorCode === "FILE_NOT_ALLOWED") {
        sendNotifyIziMessageError(t("profile.upload_file_error"));
      } else if (errorCode === "FILE_TOO_LARGE") {
        sendNotifyIziMessageError(t("profile.file_too_large"));
      } else {
        sendNotifyIziMessageError(t("profile.profile_appearance_save_error"));
      }
    }
  };

  return (
    <div
      className="tab-pane fade active show"
      id="window-tab-pane"
      role="tabpanel"
      aria-labelledby="window-tab"
      tabIndex="0"
    >
      <div className="row">
        <div className="col-lg-3">
          <h5 className="title">
            {t("settings.profile.profile_appearance.styling")}
          </h5>
        </div>
        <div className="col-lg-7">
          <div className="row g-4">
            <div className="col-12">
              <label className="form-label text-uppercase">
                {t("settings.profile.profile_appearance.status")}
              </label>
              <input
                className="form-control"
                value={status}
                onChange={(e) => setStatus(e.target.value)}
              />
            </div>
            <div className="col-12">
              <label className="form-label text-uppercase">
                {t("settings.profile.profile_appearance.theme_color")}
              </label>
              <div className="d-flex gap-2  align-items-center">
                <input
                  type="color"
                  className="form-control form-control-color"
                  id="exampleColorInput"
                  value={color}
                  onChange={(e) => setColor(e.target.value)}
                />
                <div className="btn-copy btn btn-gray btn-outline-gray bg-white">
                  {color}{" "}
                  <span className="color-info ms-5">{t("common.copy")}</span>
                </div>
              </div>
            </div>
            <div className="col-lg-6">
              <label className="form-label text-uppercase">
                {t("settings.profile.profile_appearance.schedule")}
              </label>
              <div className="d-flex align-items-center mb-4 btn-tab-box">
                <button
                  onClick={() => setSchedule(ScheduleType.ENTIRE_DAY)}
                  className={
                    schedule === ScheduleType.ENTIRE_DAY
                      ? "btn btn-gray-light btn-tab active rounded-0 rounded-start"
                      : "btn btn-gray-light btn-tab rounded-0 rounded-start"
                  }
                >
                  {t("settings.profile.profile_appearance.entire_day")}
                  <span className="color-success">●</span>
                </button>

                <button
                  onClick={() => setSchedule(ScheduleType.BUSINESS_HOURS)}
                  className={
                    schedule === ScheduleType.BUSINESS_HOURS
                      ? "btn btn-gray-light btn-tab active rounded-0 rounded-end"
                      : "btn btn-gray-light btn-tab rounded-0 rounded-end"
                  }
                >
                  {t("settings.profile.profile_appearance.business_hours")}
                  <span className="color-success">●</span>
                </button>
              </div>
              {schedule === ScheduleType.BUSINESS_HOURS && (
                <div>
                  {_.isEmpty(profileBusinessHours) ? (
                    <div className="color-gray mb-2">
                      {t(
                        "settings.profile.profile_appearance.new_setup_profile"
                      )}
                      <a
                        href={`#/profile/${profileId}/info/business-hours`}
                        className="color-info"
                      >
                        {t("settings.profile.profile_appearance.setup_now")}
                      </a>
                    </div>
                  ) : (
                    <div>
                      <div className="color-gray mb-2">
                        {t("settings.profile.profile_appearance.setup_profile")}{" "}
                        <a
                          href={`#/profile/${profileId}/info/business-hours`}
                          className="color-info"
                        >
                          {t("settings.profile.profile_appearance.change_now")}
                        </a>
                      </div>
                      <div className="border-dashed p-3 rounded-2">
                        <table className="w-100">
                          <tbody>
                            {_.map(dayKeys, (date) => {
                              const businessHour = _.get(
                                businessHours,
                                `${date}.0`,
                                {
                                  date,
                                }
                              );
                              const openHour = _.get(
                                businessHour,
                                "openHour",
                                []
                              );
                              const isClosed = _.get(
                                businessHour,
                                "isClosed",
                                false
                              );

                              return (
                                <tr key={date}>
                                  <td className="pb-2 align-top">
                                    {t(BusinessDates[date])}
                                  </td>
                                  {isClosed ? (
                                    <td className="pb-2 align-top">
                                      {t("common.closed")}
                                    </td>
                                  ) : openHour?.length ? (
                                    <td className="pb-2 align-top">
                                      {openHour.map((hour, index) => (
                                        <div key={`${index}-${hour}`}>
                                          {hour.startTime} - {hour.endTime}
                                          {index < openHour.length - 1 && (
                                            <br />
                                          )}
                                        </div>
                                      ))}
                                    </td>
                                  ) : (
                                    <td className="pb-2 align-top">--</td>
                                  )}
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <hr className="mb-4 mt-4" />
      <div className="row mb-5">
        <div className="col-lg-3">
          <h5 className="title">
            {" "}
            {t("settings.profile.profile_appearance.widget")}
          </h5>
        </div>
        <div className="col-lg-7">
          <div className="row g-4">
            <div className="col-lg-6">
              <label className="form-label text-uppercase">
                {t("settings.profile.profile_appearance.widget_size")}
              </label>
              <div className="d-flex align-items-center btn-tab-box">
                <button
                  className={
                    size === SizeWindow.SMALL
                      ? "btn btn-gray-light btn-tab active rounded-0 rounded-start"
                      : "btn btn-gray-light btn-tab rounded-0 rounded-start"
                  }
                  onClick={() => setSize(SizeWindow.SMALL)}
                >
                  {t("settings.profile.profile_appearance.small")}
                  <span className="color-success">●</span>
                </button>
                <button
                  className={
                    size === SizeWindow.MEDIUM
                      ? "btn btn-gray-light btn-tab active rounded-0"
                      : "btn btn-gray-light btn-tab rounded-0"
                  }
                  onClick={() => setSize(SizeWindow.MEDIUM)}
                >
                  {t("settings.profile.profile_appearance.medium")}
                  <span className="color-success">●</span>
                </button>
                <button
                  className={
                    size === SizeWindow.LARGE
                      ? "btn btn-gray-light btn-tab rounded-0 active rounded-end"
                      : "btn btn-gray-light btn-tab rounded-0 rounded-end"
                  }
                  onClick={() => setSize(SizeWindow.LARGE)}
                >
                  {t("settings.profile.profile_appearance.large")}
                  <span className="color-success">●</span>
                </button>
              </div>
            </div>

            <div className="col-12">
              <label className="form-label text-uppercase">
                {t("settings.profile.profile_appearance.widget_position")}
              </label>
              <div className="d-flex gap-3 btn-img-box">
                <button
                  className={
                    position === PositionWindow.LEFT
                      ? "btn btn-img active"
                      : "btn btn-img"
                  }
                  onClick={() => setPosition(PositionWindow.LEFT)}
                >
                  <img width="68px" src="/img/chat/btn1.png" alt="" />
                  <div className="mt-4">
                    {" "}
                    {t("settings.profile.profile_appearance.bottom_left")}
                  </div>
                  <i className="fa fa-check-circle"></i>
                </button>
                <button
                  className={
                    position === PositionWindow.RIGHT
                      ? "btn btn-img active"
                      : "btn btn-img"
                  }
                  onClick={() => setPosition(PositionWindow.RIGHT)}
                >
                  <img width="68px" src="/img/chat/btn2.png" alt="" />
                  <div className="mt-4">
                    {t("settings.profile.profile_appearance.bottom_right")}
                  </div>
                  <i className="fa fa-check-circle"></i>
                </button>
              </div>
            </div>

            <div className="col-12">
              <label className="form-label text-uppercase">
                {t(
                  "settings.profile.profile_appearance.widget_position_offset"
                )}
              </label>
              <input
                type="number"
                className="form-control"
                placeholder="0"
                value={offset}
                onChange={(e) => setOffset(e.target.value)}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="card-footer">
        <div className="d-flex gap-3 justify-content-center align-items-center">
          <button onClick={saveWindow} className="btn btn-primary">
            {t("common.save")}
          </button>
          <button
            onClick={() => {
              navigate(`/profile/${profileId}/detail`);
            }}
            className="btn btn-gray btn-outline-gray"
          >
            {t("common.exit")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AppearanceWindow;
